import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { fmtEUR } from '@/lib/utils';
import { getMyGroupMaterials, deleteMaterial, archiveMaterial, releaseMaterialExternal } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Search, Package, Building, MapPin, ChevronRight, Layers, DollarSign, Edit, Trash2, Archive, Eye, Globe } from 'lucide-react';

export const MyGroupPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSite, setFilterSite] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  const canManage = user && (user.role === 'admin' || user.role === 'group_admin');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (search) params.search = search;
      if (filterSite !== 'all') params.site_id = filterSite;
      if (filterStatus !== 'all') params.status = filterStatus;
      const res = await getMyGroupMaterials(params);
      setItems(res.data.items || []);
      setSites(res.data.sites || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Failed to load group materials:', e);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterSite, filterStatus]);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadData();
  }, [user, loadData]);

  const handleDelete = async () => {
    if (!selectedMaterial) return;
    try {
      await deleteMaterial(selectedMaterial.material_id);
      toast.success(t('myMaterials.deleted') || 'Material gelöscht');
      setDeleteDialogOpen(false);
      setSelectedMaterial(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleArchive = async () => {
    if (!selectedMaterial) return;
    try {
      await archiveMaterial(selectedMaterial.material_id);
      toast.success(t('myMaterials.archived') || 'Material archiviert');
      setArchiveDialogOpen(false);
      setSelectedMaterial(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Archivieren');
    }
  };

  const handleReleaseExternal = async (materialId) => {
    try {
      await releaseMaterialExternal(materialId);
      toast.success('Material wurde für externe Sichtbarkeit freigegeben');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Freigabe');
    }
  };

  const hasInternalSkus = (item) => {
    const now = new Date().toISOString();
    return item.skus?.some(s => s.internal_only_until_date && s.internal_only_until_date > now);
  };

  const statusColors = {
    available: 'bg-green-100 text-green-800',
    reserved: 'bg-blue-100 text-blue-800',
    archived: 'bg-gray-100 text-gray-800',
    mixed: 'bg-orange-100 text-orange-800',
  };

  const statusLabel = (s) => {
    if (s === 'mixed') return 'Mixed';
    const key = `status.${s}`;
    const val = t(key);
    return val === key ? s : val;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="my-group-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-manrope text-3xl font-bold flex items-center gap-3" data-testid="group-title">
            <Layers className="h-8 w-8 text-violet-500" />
            {t('group.title')}
          </h1>
          <p className="text-muted-foreground mt-1">{t('group.subtitle')}</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="group-search-input"
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10"
            />
          </div>
          <Select value={filterSite} onValueChange={(v) => { setFilterSite(v); setPage(1); }}>
            <SelectTrigger className="w-[180px]" data-testid="group-site-filter">
              <Building className="h-4 w-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder={t('group.filterSite')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('group.allSites')}</SelectItem>
              {sites.map(s => (
                <SelectItem key={s.site_id} value={s.site_id}>{s.site_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(1); }}>
            <SelectTrigger className="w-[160px]" data-testid="group-status-filter">
              <SelectValue placeholder={t('group.filterStatus')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('myMaterials.allStatuses')}</SelectItem>
              <SelectItem value="available">{t('status.available')}</SelectItem>
              <SelectItem value="reserved">{t('status.reserved')}</SelectItem>
              <SelectItem value="archived">{t('myMaterials.statusArchived')}</SelectItem>
            </SelectContent>
          </Select>
          <Badge variant="secondary" className="text-sm px-3 py-1.5">{total} {t('dashboard.items')}</Badge>
        </div>

        {/* Materials list */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16" data-testid="group-empty">
            <Package className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground">{t('group.noMaterials')}</h3>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <Card key={item.material_id} className={`hover:shadow-md transition-shadow ${item.status === 'archived' ? 'opacity-60' : ''}`} data-testid={`group-material-${item.material_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/material/${item.material_id}`)}>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium truncate">{item.common_title || item.manufacturer_part_number}</h3>
                        {item.is_own && (
                          <Badge variant="outline" className="text-xs text-violet-600 border-violet-300 shrink-0">{t('group.ownMaterial')}</Badge>
                        )}
                        {hasInternalSkus(item) && (
                          <Badge className="text-xs bg-blue-500 text-white shrink-0" data-testid={`group-internal-badge-${item.material_id}`}>
                            Intern
                          </Badge>
                        )}
                        <Badge className={`text-xs ${statusColors[item.status] || statusColors.available}`}>
                          {statusLabel(item.status)}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {item.manufacturer_name} | {item.manufacturer_part_number}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Package className="h-3 w-3" />
                          {t('group.skuCount', { count: item.sku_count })}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          {'\u20AC'}{fmtEUR(item.total_value_kEUR || 0)}
                        </span>
                        {item.sites?.map(s => (
                          <span key={s.site_id} className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {s.site_name}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => navigate(`/material/${item.material_id}`)} title={t('common.view') || 'Ansehen'} data-testid={`group-view-btn-${item.material_id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canManage && (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => navigate(`/material/${item.material_id}/edit`)} title={t('common.edit') || 'Bearbeiten'} data-testid={`group-edit-btn-${item.material_id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          {hasInternalSkus(item) && (
                            <Button variant="ghost" size="icon" onClick={() => handleReleaseExternal(item.material_id)} title="Jetzt extern freigeben" data-testid={`group-release-btn-${item.material_id}`}>
                              <Globe className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          {item.status !== 'archived' && (
                            <Button variant="ghost" size="icon" onClick={() => { setSelectedMaterial(item); setArchiveDialogOpen(true); }} title={t('myMaterials.archive') || 'Archivieren'} data-testid={`group-archive-btn-${item.material_id}`}>
                              <Archive className="h-4 w-4 text-amber-500" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => { setSelectedMaterial(item); setDeleteDialogOpen(true); }} title={t('common.delete') || 'Löschen'} data-testid={`group-delete-btn-${item.material_id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </>
                      )}
                      {!canManage && <ChevronRight className="h-5 w-5 text-muted-foreground" />}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="group-prev-page">
              {t('admin.auditPrevPage')}
            </Button>
            <span className="flex items-center text-sm text-muted-foreground px-3">
              {page} / {totalPages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="group-next-page">
              {t('admin.auditNextPage')}
            </Button>
          </div>
        )}
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="group-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('myMaterials.deleteTitle') || 'Material löschen?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('myMaterials.deleteDesc') || 'Das Material und alle zugehörigen Bestände werden unwiderruflich gelöscht.'}
              <br />
              <strong>{selectedMaterial?.common_title}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="group-delete-cancel">{t('common.cancel') || 'Abbrechen'}</AlertDialogCancel>
            <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={handleDelete} data-testid="group-delete-confirm">
              {t('common.delete') || 'Löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent data-testid="group-archive-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('myMaterials.archiveTitle') || 'Material archivieren?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('myMaterials.archiveDesc') || 'Das Material wird archiviert und ist nicht mehr im Marktplatz sichtbar.'}
              <br />
              <strong>{selectedMaterial?.common_title}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="group-archive-cancel">{t('common.cancel') || 'Abbrechen'}</AlertDialogCancel>
            <AlertDialogAction className="bg-amber-500 hover:bg-amber-600" onClick={handleArchive} data-testid="group-archive-confirm">
              {t('myMaterials.archive') || 'Archivieren'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
